import React, { useState, useRef, useEffect } from "react";
import { UserDropdown } from "./UserDropdown";
import { signOutUser } from "src/api/firebase";
import { useAuth } from "src/contexts/AuthContext";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";

const userIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-3.31 0-10 1.67-10 5v2h20v-2c0-3.33-6.69-5-10-5z"
      fill="var(--color-primary)"
    />
  </svg>
);

interface GlobalHeaderProps {
  companyName?: string;
  companyLogo?: string;
  companyLogoHeaderClassName?: string;
  subscriptionLevel: string;
  hideUserIcon?: boolean;
}

const logo = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="29"
    viewBox="0 0 18 29"
    fill="none"
  >
    <path
      d="M2.97171 9.06429L17.9503 1.67143C17.9503 5.65715 18.0145 5.91429 17.5003 7.07143C17.1026 7.96621 16.3739 9.02575 15.3788 9.57857C11.9074 11.5071 8.43598 12.9857 5.73598 14.2714C4.76767 14.7325 4.00026 15.1071 3.42169 15.75C2.25398 17.0475 2.17884 18.45 2.32884 19.0286C0.580268 17.0743 0.0788383 15.5571 0.0788424 15.0429C-0.435441 11.8929 1.68598 9.77143 2.97171 9.06429Z"
      fill="#1C1C1C"
    />
    <path
      d="M2.97171 18.45L17.9503 11.0571C17.9503 15.0429 18.0145 15.3 17.5003 16.4571C17.1026 17.3519 16.3739 18.4115 15.3788 18.9643C11.9074 20.8929 8.43598 22.3714 5.73598 23.6571C4.76767 24.1182 4.00026 24.4929 3.42169 25.1357C2.25398 26.4332 2.17884 27.8357 2.32884 28.4143C0.580268 26.46 0.0788383 24.9429 0.0788424 24.4286C-0.435441 21.2786 1.68598 19.1571 2.97171 18.45Z"
      fill="#1C1C1C"
    />
    <path
      d="M11.5233 24.6903C10.9744 24.3746 10.9744 23.5826 11.5234 23.2669L17.9999 19.5429L17.9999 28.4143L11.5233 24.6903Z"
      fill="#1C1C1C"
    />
    <path
      d="M4.11429 6.3H2.25V3.40714C1.63286 4.02429 0.492857 4.22143 0 4.24286V2.37857C0.385714 2.37857 0.981818 2.25 1.54286 1.47857C2.1039 0.707143 2.18571 0.3 2.25 0H4.11429C4.13571 0.214286 4.29429 0.81 4.75714 1.47857C5.22 2.14714 6.02143 2.35714 6.36429 2.37857V4.24286C5.52857 4.24286 4.37143 3.68571 4.11429 3.40714V6.3Z"
      fill="#1C1C1C"
    />
  </svg>
);

const GlobalHeader: React.FC<GlobalHeaderProps> = ({
  companyName,
  companyLogo,
  companyLogoHeaderClassName = "",
  subscriptionLevel,
  hideUserIcon = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { userDoc } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = usePartnerAwareNavigate();

  // useEffect(() => {
  //     // Check if we need to navigate after refresh
  //     const shouldNavigateHome = sessionStorage.getItem("navigateHome");
  //     if (shouldNavigateHome) {
  //         sessionStorage.removeItem("navigateHome");
  //         navigate("/");
  //     }
  // }, [navigate]);

  const goToHome = () => {
    navigate("/");
    // Refresh the page after navigation is initiated
    window.location.reload();
  };

  const handleSignOut = async () => {
    console.log("Signing out...");
    await signOutUser();
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Get subscription display text and style based on level
  const getSubscriptionStyles = () => {
    const levelLower = subscriptionLevel.toLowerCase();
    if (levelLower.includes("plus") || levelLower.includes("plus")) {
      return {
        text: "WeKruit Plus",
        bgColor: "bg-purple-100",
        textColor: "text-purple-800",
      };
    } else if (levelLower.includes("free")) {
      return {
        text: "Free Plan",
        bgColor: "bg-gray-100",
        textColor: "text-gray-800",
      };
    } else if (levelLower.includes("pro")) {
      return {
        text: "WeKruit Pro",
        bgColor: "bg-indigo-100",
        textColor: "text-indigo-800",
      };
    } else if (levelLower.includes("business")) {
      return {
        text: "WeKruit Business",
        bgColor: "bg-indigo-100",
        textColor: "text-indigo-800",
      };
    } else if (levelLower.includes("enterprise")) {
      return {
        text: "Enterprise Plan",
        bgColor: "bg-blue-100",
        textColor: "text-blue-800",
      };
    } else {
      return {
        text: "Loading...",
        bgColor: "bg-gray-100",
        textColor: "text-gray-800",
      };
    }
  };

  const subscriptionStyles = getSubscriptionStyles();

  return (
    <header
      className="bg-white border-b border-gray-200 shadow-lg"
      style={{ boxShadow: "0px 0px 4px 0px rgba(176, 176, 176, 0.25)" }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side: Logo */}
          <div className="flex items-center cursor-pointer" onClick={goToHome}>
            {/* Show WeKruit logo plus partner logo if in partner context */}
            <div className="h-8 w-auto mr-2">{logo}</div>

            {companyLogo && (
              <div className="flex items-center">
                <div className="mx-2 text-gray-400">×</div>
                <img
                  src={companyLogo}
                  alt={`${companyName} Logo`}
                  className={`h-8 ${companyLogoHeaderClassName}`}
                />
              </div>
            )}
          </div>

          {/* Center: Company Name, Collaboration Status, and Subscription Badge */}
          {/* <div className="flex items-center space-x-4">
            Subscription Badge
            <div
              onClick={() => navigate("/subscription")}
              className={`rounded-full px-4 py-1.5 mt-2 cursor-pointer hover:opacity-80 transition-opacity duration-200 ${subscriptionStyles.bgColor}`}
            >
              <span
                className={`font-medium text-sm ${subscriptionStyles.textColor}`}
              >
                {subscriptionStyles.text}
              </span>
            </div>

            {companyName && (
              <div className="hidden md:block">
                <span className="text-white font-medium text-lg">
                  {companyName}
                </span>
              </div>
            )}

            {(companyName || companyLogo) && (
              <div className="hidden md:flex items-center bg-blue-500 rounded-full px-4 py-1">
                <span className="text-blue-100 text-sm">
                  Collaborating with
                </span>
                <span className="ml-1 text-white font-medium text-sm">
                  WeKruit
                </span>
              </div>
            )}
          </div> */}

          {/* Right side: User Menu */}
          {!hideUserIcon && (
            <div className="relative">
              <button
                ref={buttonRef}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary focus:ring-white"
              >
                {userIcon}
              </button>

              <div className="relative" ref={dropdownRef}>
                {isDropdownOpen && (
                  <div className="absolute right-[1rem] mt-1 z-50">
                    <UserDropdown
                      accountName={userDoc?.survey.name || "User"}
                      email={userDoc?.email || ""}
                      onSignOut={handleSignOut}
                      subscriptionLevel={subscriptionLevel}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default GlobalHeader;
